import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'js/design_system/Button';
import { Dropdown } from 'js/design_system/forms/Dropdown';
import {
  SearchIcon,
  SearchInput,
  SearchSection,
  InputWrapper,
  StyledDropdown,
  SearchInputAndDropdownContainer,
  SearchButtonContainer,
} from './SearchNavbarComponents';

export type JournalOption = {
  id: string;
  name: string;
};

interface NavbarSearchContentProps {
  initialQuery?: string;
  initialJournal?: JournalOption;
  journalOptions: JournalOption[];
}

const NavbarSearchContent: React.FC<NavbarSearchContentProps> = ({
  initialQuery = '',
  initialJournal = { id: 'all', name: 'All journals' },
  journalOptions,
}) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [selectedJournal, setSelectedJournal] =
    useState<JournalOption>(initialJournal);

  // Ref for auto-focusing the search input
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the search input when component mounts
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const handleJournalChange = (option: JournalOption) => {
    if (option && typeof option === 'object' && 'id' in option) {
      setSelectedJournal(option);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();

    if (searchQuery.trim()) {
      params.append('q', searchQuery);
    }

    if (selectedJournal.id !== 'all') {
      params.append('journal', selectedJournal.id);
    }

    window.location.href = `/journals/search?${params.toString()}`;
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <SearchSection role="search">
      <SearchInputAndDropdownContainer>
        <InputWrapper>
          <SearchIcon />
          <SearchInput
            ref={searchInputRef}
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            aria-label="Search articles"
            autoComplete="off"
          />
        </InputWrapper>

        <StyledDropdown>
          <div
            onMouseDown={(e) => {
              e.stopPropagation();
              if (document.activeElement === searchInputRef.current) {
                e.preventDefault();
              }
            }}
          >
            <Dropdown
              placeholder="Select journal"
              value={selectedJournal}
              options={journalOptions}
              renderOption={(option) => option.name}
              renderValue={(option) => option.name}
              onChange={handleJournalChange}
              keyExtractor={(option) => option.id}
            />
          </div>
        </StyledDropdown>
      </SearchInputAndDropdownContainer>

      <SearchButtonContainer>
        <Button size="sm" onClick={handleSearch}>
          Search
        </Button>
      </SearchButtonContainer>
    </SearchSection>
  );
};

export default NavbarSearchContent;
